import { useQuery } from '@apollo/client';
import { getId } from '@fluentui/react';
import {
  Combobox,
  Option,
  Field,
  FluentProvider,
  Spinner,
  webLightTheme,
} from '@fluentui/react-components';
import React, { useState } from 'react';
import GET_ACQUISITION_QUERY from '../../utils/statApi/AcquisitionApi';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';
import { IAcquisition } from './types';

interface IComboBoxOption {
  key: string;
  text: string;
}

interface IAcquistionProps {
  label: string;
  disabled: boolean;
  onChange: (value: IComboBoxOption) => void;
  required: boolean;
  errorMessage?: string;
  defaultValue?: IAcquisition;
}

const AcquisitionSelector = ({
  label,
  disabled,
  onChange,
  required,
  errorMessage,
  defaultValue,
}: IAcquistionProps): JSX.Element => {
  const [inputValue, setInputValue] = useState(defaultValue?.text || '');
  const [options, setOptions] = useState([]);

  const { loading, error } = useQuery(GET_ACQUISITION_QUERY, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      setOptions(data.acquisitions);
      if (defaultValue) {
        setInputValue(defaultValue.text);
      }
    },
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setInputValue(value);
    if (!value.trim()) {
      onChange(null);
    }
  };

  const handleOptionSelect = (
    event: React.SyntheticEvent,
    data: { optionValue?: string; optionText?: string },
  ): void => {
    if (data.optionValue) {
      // Selected from dropdown
      const selectedOption = options.find((opt) => opt.key === data.optionValue);
      if (selectedOption) {
        setInputValue(selectedOption.text);
        onChange(selectedOption);
      }
    }
  };

  const handleBlur = (): void => {
    if (inputValue.trim()) {
      const existingOption = options.find(
        (opt) => opt.text.toLowerCase() === inputValue.trim().toLowerCase(),
      );
      if (existingOption) {
        setInputValue(existingOption.text);
        onChange(existingOption);
      } else {
        // Create new option for free-form entry
        const newOption = { key: getId(), text: inputValue.trim() };
        setOptions((prevOptions) => [...prevOptions, newOption]);
        setInputValue(newOption.text);
        onChange(newOption);
      }
    } else {
      onChange(null);
    }
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <LoadingErrorMessage loading={loading} error={error} />
      {loading && <Spinner aria-label="Loading" />}
      {!loading && !error && (
        <Field label={label} required={required} validationMessage={errorMessage}>
          <Combobox
            value={inputValue}
            onInput={handleInputChange}
            onOptionSelect={handleOptionSelect}
            onBlur={handleBlur}
            freeform
            disabled={disabled}
          >
            {options.map((option) => (
              <Option key={option.key} value={option.key} text={option.text}>
                {option.text}
              </Option>
            ))}
          </Combobox>
        </Field>
      )}
    </FluentProvider>
  );
};

export default AcquisitionSelector;
